/**
 * CertSalesPage: the public BAM Certification page, at /#/certification.
 *
 * Built for paid traffic (Coleman, 2026-11-02): someone off a Meta ad who may
 * not know By Any Means yet has to be able to understand the cert, trust who
 * is behind it, and pay, all without an account or a sales call. Booking a
 * call stays available, but as the backup to "Enroll now", not the gate.
 *
 * Order is the order a cold visitor's questions come in: what is it, who is
 * behind it, is it for me, what's in it, how does it work, what do I get, do
 * other coaches vouch for it, what does it cost, what if it's not for me,
 * and then everything else (FAQ, a call, a cheaper way in).
 *
 * Web only. Pricing and outside payment never render in the iOS app (App
 * Store rule): App.js bounces native users away, and this returns null on
 * native as a backstop.
 *
 * Voice: plain words, no em dashes in coach-facing copy.
 */
import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Check, ChevronDown, Loader2, ShieldCheck, Phone, Mail, PlayCircle, Compass } from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAM_LOGO_PNG } from "../constants/branding";
import {
  CERT_UNITS, CERT_UNIT_SUMMARIES, CERT_PERSONAS, CERT_PATHWAY, CERT_INCLUDED, CERT_TESTIMONIALS, CERT_FAQ,
} from "../certification/constants";
import { supabase } from "../../supabaseClient";
import { logEvent } from "../utils/analytics";
import { isNative } from "../../utils/platform";
import { metaTrack } from "../../utils/metaPixel";
import { captureAdSource } from "../../utils/adSource";
import { CallModal, ObjectionPicker } from "./certSalesParts";
import { startCertCheckout, certPricing, fetchCountry, CERT_PRICE, CERT_3PAY_AMOUNT, CERT_3PAY_COUNT, INTL_PRICE, INTL_3PAY_AMOUNT } from "../utils/certCheckout";

const BG = "#0A0A0A";
const CARD = "#121212";
const LINE = "rgba(255,255,255,0.08)";
const TEXT = "#F5F0E8";
const MID = "#B8B2A4";
const DIM = "#7D786E";
const HEAD = "'Bebas Neue', sans-serif";

// The promo cut that lives on byanymeanscoaches.com/certification.
const PROMO_VIDEO = "https://byanymeanscoaches.com/video/certification-promo.mp4";
const PROMO_POSTER = "https://byanymeanscoaches.com/video/certification-promo.jpg";
const CONTACT_EMAIL = "info@byanymeanscoaches.com";
const CONTACT_PHONE = "(786) 756-7862";

// Numbers a stranger can check us on. Keep these matching the rest of the
// site (LandingPage founder bio, BuyCertPage social proof).
export const BRAND_STATS = [
  { n: "100+", label: "Certified coaches" },
  { n: "35+", label: "Countries worked in" },
  { n: "500+", label: "Players trained each year" },
  { n: "2", label: "#1 Amazon coaching books" },
];

const money = (n) => `$${n.toLocaleString("en-US")}`;
const units = CERT_UNITS.filter((u) => u.id >= 1 && u.id <= 9);

function useIsMobile() {
  const get = () => (typeof window !== "undefined" ? window.innerWidth < 760 : false);
  const [m, setM] = useState(get);
  useEffect(() => {
    const on = () => setM(get());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return m;
}

export function CertSalesPage() {
  const isMobile = useIsMobile();
  const [plan, setPlan] = useState("full");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [owns, setOwns] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [openUnit, setOpenUnit] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [code, setCode] = useState("");
  // Outside the US: $400 off, shown loud (Coleman: "they need to know
  // they're getting a big discount"). null until api/geo answers.
  const [country, setCountry] = useState(null);
  const price = certPricing(country);
  useEffect(() => { let on = true; fetchCountry().then((c) => { if (on) setCountry(c); }); return () => { on = false; }; }, []);
  const [codeOpen, setCodeOpen] = useState(false);

  const params = useMemo(() => {
    const out = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const inHash = (typeof window !== "undefined" ? window.location.hash : "").split("?")[1];
    if (inHash) new URLSearchParams(inHash).forEach((v, k) => out.set(k, v));
    return out;
  }, []);
  const justBought = params.get("cert_purchased") === "1";

  useEffect(() => {
    captureAdSource();
    if (params.get("cert_cancelled") === "1") {
      setError("Checkout was cancelled. You weren't charged. Pick up whenever you're ready.");
      logEvent("cert_purchase_cancelled", { from: "cert_sales_page" });
    }
    if (params.get("call") === "1") setCallOpen(true);
    if (justBought) {
      const purchasedPlan = params.get("plan") === "3pay" ? "3pay" : "full";
      const paidIntl = params.get("intl") === "1";
      const value = purchasedPlan === "3pay" ? (paidIntl ? INTL_3PAY_AMOUNT : CERT_3PAY_AMOUNT) : (paidIntl ? INTL_PRICE : CERT_PRICE);
      // Same event id as the server-side Purchase, so Meta counts it once.
      metaTrack("Purchase", { value, currency: "USD", content_name: "BAM Certification" }, params.get("sid") || undefined);
      logEvent("cert_purchase_success", { from: "cert_sales_page", plan: purchasedPlan, guest: params.get("guest") === "1" });
    } else {
      metaTrack("ViewContent", { content_name: "BAM Certification", value: CERT_PRICE, currency: "USD" });
      logEvent("cert_sales_view", {});
    }
    let cancelled = false;
    supabase.auth.getUser().then(async ({ data }) => {
      const user = data?.user;
      if (!user || cancelled) return;
      const { data: rows } = await supabase.from("profiles").select("cert_purchased").eq("id", user.id).limit(1);
      if (!cancelled && rows?.[0]?.cert_purchased) setOwns(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [params, justBought]);

  const enroll = async (where) => {
    if (owns) { window.location.hash = "#/certified"; return; }
    setBusy(true);
    setError(null);
    const value = plan === "3pay" ? price.pay : price.full;
    metaTrack("InitiateCheckout", { value, currency: "USD", content_name: "BAM Certification" });
    logEvent("cert_sales_checkout_start", { plan, where, code: code ? code.trim().toUpperCase() : null, intl: price.intl, country });
    const r = await startCertCheckout({ plan, returnHash: "#/certification", code, country });
    if (r.alreadyPurchased) { window.location.hash = "#/certified"; return; }
    if (r.url) { window.location.href = r.url; return; }
    setError(r.error);
    setBusy(false);
  };

  const scrollToPrice = (where) => {
    logEvent("cert_sales_cta_click", { where });
    document.getElementById("cert-price")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openCall = (where) => {
    logEvent("cert_sales_call_click", { where });
    setCallOpen(true);
  };

  if (isNative()) return null;
  if (justBought) return <ThankYou guest={params.get("guest") === "1"} plan={params.get("plan")} />;

  const pad = isMobile ? "0 18px" : "0 32px";
  const section = { maxWidth: 1080, margin: "0 auto", padding: pad };

  return (
    <div style={{ background: BG, color: TEXT, minHeight: "100vh", fontFamily: "Inter, system-ui, sans-serif", paddingBottom: isMobile ? 84 : 0 }}>
      {/* Top bar */}
      <div style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(10,10,10,0.92)", backdropFilter: "blur(8px)", borderBottom: `1px solid ${LINE}` }}>
        <div style={{ ...section, display: "flex", alignItems: "center", justifyContent: "space-between", height: 60 }}>
          <a href="https://byanymeanscoaches.com" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: TEXT }}>
            <img src={BAM_LOGO_PNG} alt="By Any Means" style={{ height: 30, width: "auto" }} />
            {!isMobile && <span style={{ fontFamily: HEAD, letterSpacing: 1.5, fontSize: 18 }}>BY ANY MEANS COACHES</span>}
          </a>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {!isMobile && <button onClick={() => openCall("top_bar")} style={ghostBtn}>Book a call</button>}
            <button onClick={() => scrollToPrice("top_bar")} style={{ ...goldBtn, padding: "9px 16px", fontSize: 13 }}>Enroll</button>
          </div>
        </div>
      </div>

      {/* 1. Hero */}
      <div style={{ ...section, paddingTop: isMobile ? 40 : 72, paddingBottom: 32, textAlign: "center" }}>
        <Eyebrow>The BAM Coaches Certification</Eyebrow>
        <h1 style={{ fontFamily: HEAD, fontSize: isMobile ? 46 : 76, lineHeight: 0.95, letterSpacing: 1, margin: "14px 0 18px" }}>
          Transform your coaching.<br /><span style={{ color: GOLD }}>Elevate every player.</span>
        </h1>
        <p style={{ ...lead, maxWidth: 680, margin: "0 auto 26px" }}>
          The player development course behind 100+ certified coaches. Nine units built on the science of how skills are
          actually learned, a real test reviewed by our staff, and a credential that shows it.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={() => scrollToPrice("hero")} style={goldBtn}>
            {owns ? "Open my cert" : `Enroll now, ${money(price.full)}`} <ArrowRight size={16} strokeWidth={2.5} />
          </button>
          <button onClick={() => openCall("hero")} style={ghostBtn}>Talk to our team first</button>
        </div>
        <div style={{ marginTop: 14 }}>
          <a href="#/cert-path" onClick={() => logEvent("cert_path_link_click", { where: "hero" })} style={{ color: GOLD, fontSize: 14, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
            <Compass size={15} /> Not sure yet? Find your path in 60 seconds
          </a>
        </div>
        {price.intl && !owns && (
          <div style={{ marginTop: 16, display: "inline-flex", alignItems: "center", gap: 10, padding: "8px 14px", borderRadius: 999, background: `${GOLD}1A`, border: `1px solid ${GOLD}55` }}>
            <span style={{ textDecoration: "line-through", color: DIM }}>{money(price.wasFull)}</span>
            <span style={{ color: GOLD, fontWeight: 700, fontSize: 14 }}>International coach pricing: you save {money(price.saving)}</span>
          </div>
        )}
        <div style={{ marginTop: 14, fontSize: 13, color: DIM }}>
          Or {CERT_3PAY_COUNT} payments of {money(price.pay)} · Klarna available · 7-day refund window
        </div>
      </div>

      {/* 2. Video */}
      <div style={{ ...section, paddingBottom: 56 }}>
        <div style={{ borderRadius: 14, overflow: "hidden", border: `1px solid ${LINE}`, background: "#000" }}>
          <video
            src={PROMO_VIDEO} poster={PROMO_POSTER} controls playsInline preload="none"
            onPlay={() => logEvent("cert_sales_video_play", {})}
            style={{ display: "block", width: "100%", aspectRatio: "16 / 9", background: "#000" }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginTop: 10, color: DIM, fontSize: 13 }}>
          <PlayCircle size={14} /> Inside the certification
        </div>
      </div>

      {/* 3. Who's behind it */}
      <Section title="Who's behind it" eyebrow="By Any Means Basketball" isMobile={isMobile}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "220px 1fr", gap: isMobile ? 22 : 36, alignItems: "start", background: CARD, border: `1px solid ${LINE}`, borderRadius: 14, padding: isMobile ? 22 : 36 }}>
          <img src="/landing/coleman.jpg" alt="Coleman Ayers" loading="lazy" style={{ width: isMobile ? 150 : "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 10, margin: isMobile ? "0 auto" : 0, display: "block" }} />
          <div>
            <div style={{ fontFamily: HEAD, fontSize: 26, letterSpacing: 1 }}>Coleman Ayers, founder</div>
            <p style={para}>
              By Any Means is a player development company built by Coleman Ayers and a network of 15+ coaches who train
              500+ players every year. Over the past decade Coleman has trained athletes from youth to the professional
              level, consulted with staffs from high school programs to college and NBA environments, and worked alongside
              coaches in 35+ countries.
            </p>
            <p style={para}>
              He's written two books on player development and modern coaching, both #1 in Basketball Coaching on Amazon.
              The certification is that work, organized into a course: the same ideas our own coaches use every day.
            </p>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 12, marginTop: 16 }}>
          {BRAND_STATS.map((s) => (
            <div key={s.label} style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 12, padding: "18px 16px", textAlign: "center" }}>
              <div style={{ fontFamily: HEAD, fontSize: 38, color: GOLD, lineHeight: 1 }}>{s.n}</div>
              <div style={{ fontSize: 13, color: MID, marginTop: 6 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 4. Who it's for */}
      <Section title="Built for coaches who want more" eyebrow="Who it's for" isMobile={isMobile}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          {CERT_PERSONAS.map((p) => (
            <div key={p.id} style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 12, padding: 20 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>{p.title}</div>
              <div style={{ color: MID, fontSize: 14, lineHeight: 1.6 }}>{p.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 5. Curriculum */}
      <Section title="What you'll learn" eyebrow="9 core units" isMobile={isMobile}>
        <div style={{ border: `1px solid ${LINE}`, borderRadius: 12, overflow: "hidden" }}>
          {units.map((u, i) => {
            const open = openUnit === u.id;
            return (
              <div key={u.id} style={{ borderTop: i ? `1px solid ${LINE}` : "none", background: CARD }}>
                <button onClick={() => setOpenUnit(open ? null : u.id)} style={rowBtn}>
                  <span style={{ fontFamily: HEAD, color: GOLD, fontSize: 18, width: 70, flexShrink: 0, textAlign: "left" }}>Unit {String(u.id).padStart(2, "0")}</span>
                  <span style={{ flex: 1, textAlign: "left", fontWeight: 600 }}>{u.title}</span>
                  <ChevronDown size={18} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", color: DIM }} />
                </button>
                {open && <div style={{ padding: "0 20px 18px 90px", color: MID, fontSize: 14, lineHeight: 1.65 }}>{CERT_UNIT_SUMMARIES[u.id]}</div>}
              </div>
            );
          })}
        </div>
      </Section>

      {/* 6. How it works */}
      <Section title="How it works" eyebrow="The pathway" isMobile={isMobile}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12 }}>
          {CERT_PATHWAY.map((s) => (
            <div key={s.id} style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 12, padding: 22 }}>
              <div style={{ fontFamily: HEAD, fontSize: 40, color: GOLD, lineHeight: 1 }}>{s.id}</div>
              <div style={{ fontWeight: 700, margin: "8px 0 6px" }}>{s.title}</div>
              <div style={{ color: MID, fontSize: 14, lineHeight: 1.6 }}>{s.desc}</div>
              <div style={{ color: DIM, fontSize: 12, marginTop: 10 }}>{s.meta}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 7. What you get */}
      <Section title="Everything that comes with it" eyebrow="What you get" isMobile={isMobile}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
          {CERT_INCLUDED.map((it) => (
            <div key={it.title} style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 12, padding: 18, display: "flex", gap: 12 }}>
              <Check size={18} color={GOLD} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{it.title}</div>
                <div style={{ color: MID, fontSize: 13, lineHeight: 1.55, marginTop: 4 }}>{it.desc}</div>
              </div>
            </div>
          ))}
          <div style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 12, padding: 18, display: "flex", gap: 12 }}>
            <Check size={18} color={GOLD} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>6 months of the BAM Coaches Platform</div>
              <div style={{ color: MID, fontSize: 13, lineHeight: 1.55, marginTop: 4 }}>The full platform, free while you work through the cert.</div>
            </div>
          </div>
        </div>
      </Section>

      {/* 8. Coaches on it */}
      {CERT_TESTIMONIALS.length > 0 && (
        <Section title="From coaches who've done it" eyebrow="BAM Certified" isMobile={isMobile}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile || CERT_TESTIMONIALS.length === 1 ? "1fr" : "1fr 1fr", gap: 12 }}>
            {CERT_TESTIMONIALS.map((t) => (
              <figure key={t.name} style={{ margin: 0, background: CARD, border: `1px solid ${LINE}`, borderRadius: 12, padding: isMobile ? 22 : 30 }}>
                <blockquote style={{ margin: 0, fontSize: 16, lineHeight: 1.7, color: TEXT }}>"{t.quote}"</blockquote>
                <figcaption style={{ marginTop: 16, color: GOLD, fontWeight: 700 }}>{t.name} <span style={{ color: DIM, fontWeight: 400 }}>· {t.role}</span></figcaption>
              </figure>
            ))}
          </div>
        </Section>
      )}

      {/* 9. Price */}
      <div id="cert-price" style={{ ...section, paddingTop: 64, scrollMarginTop: 70 }}>
        <div style={{ maxWidth: 560, margin: "0 auto", background: CARD, border: `1px solid ${GOLD}55`, borderRadius: 16, padding: isMobile ? 24 : 36, textAlign: "center" }}>
          <Eyebrow>Your investment</Eyebrow>
          {price.intl && (
            <div style={{ margin: "14px auto 0", padding: "10px 14px", borderRadius: 10, background: `${GOLD}1A`, border: `1px solid ${GOLD}55` }}>
              <div style={{ color: GOLD, fontWeight: 800, fontSize: 15 }}>International coach pricing: {money(price.saving)} off</div>
              <div style={{ color: MID, fontSize: 12, marginTop: 4 }}>Applied automatically for coaches outside the US. No code needed.</div>
            </div>
          )}
          <div role="radiogroup" aria-label="Payment option" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, margin: "18px 0 20px" }}>
            <PlanOption active={plan === "full"} onClick={() => setPlan("full")} title={money(price.full)} was={price.wasFull && money(price.wasFull)} sub="Pay in full" />
            <PlanOption active={plan === "3pay"} onClick={() => setPlan("3pay")} title={`${CERT_3PAY_COUNT} × ${money(price.pay)}`} was={price.wasPay && `${CERT_3PAY_COUNT} × ${money(price.wasPay)}`} sub="Monthly, stops after 3" />
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px", textAlign: "left", display: "grid", gap: 8 }}>
            {[
              "Lifetime access to the full course",
              "Every submission reviewed by our staff",
              "The BAM Certified credential",
              "6 months of the platform included",
              plan === "full" ? "Card, or Klarna to pay over time where offered" : "Card. Same date each month, then it stops",
            ].map((t) => (
              <li key={t} style={{ display: "flex", gap: 10, color: MID, fontSize: 14 }}><Check size={16} color={GOLD} style={{ flexShrink: 0, marginTop: 2 }} />{t}</li>
            ))}
          </ul>
          <button onClick={() => enroll("price_card")} disabled={busy} style={{ ...goldBtn, width: "100%", justifyContent: "center", opacity: busy ? 0.7 : 1 }}>
            {busy ? <Loader2 size={16} className="spin" /> : null}
            {owns ? "Open my cert" : busy ? "Opening secure checkout" : plan === "3pay" ? `Start with ${money(price.pay)} today` : `Enroll now, ${money(price.full)}`}
            {!busy && <ArrowRight size={16} strokeWidth={2.5} />}
          </button>
          {error && <div role="alert" style={{ marginTop: 12, color: "#F2A3A3", fontSize: 13 }}>{error}</div>}
          <div style={{ marginTop: 12, fontSize: 12, color: DIM }}>No account needed to pay. We set it up and email you a link.</div>
          {codeOpen ? (
            <input
              value={code} onChange={(e) => setCode(e.target.value)} placeholder="Discount code" aria-label="Discount code"
              autoCapitalize="characters" spellCheck={false}
              style={{ marginTop: 12, width: "100%", boxSizing: "border-box", padding: "11px 14px", borderRadius: 10, border: `1px solid ${LINE}`, background: "#0d0d0d", color: TEXT, fontSize: 15, letterSpacing: 1 }}
            />
          ) : (
            <button onClick={() => setCodeOpen(true)} style={{ marginTop: 8, background: "none", border: "none", color: MID, fontSize: 12, textDecoration: "underline", cursor: "pointer" }}>
              Have a discount code?
            </button>
          )}
        </div>

        {/* 10. Refund */}
        <div style={{ maxWidth: 560, margin: "14px auto 0", display: "flex", gap: 12, alignItems: "flex-start", padding: "16px 18px", border: `1px solid ${LINE}`, borderRadius: 12 }}>
          <ShieldCheck size={20} color={GOLD} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: MID, lineHeight: 1.6 }}>
            <strong style={{ color: TEXT }}>7-day refund window.</strong> Start the course. If it's not for you, email us within 7 days
            for a full refund, as long as you haven't finished more than one unit or downloaded the certificate.
          </div>
        </div>
      </div>

      {/* 10b. What's holding you back? The objections a sales call handles. */}
      <Section title="What's holding you back?" eyebrow="Straight answers" isMobile={isMobile}>
        <ObjectionPicker
          from="cert_page"
          enrollLabel={`Enroll now, ${money(price.full)}`}
          onEnroll={(where) => { scrollToPrice(where); }}
          onCall={openCall}
        />
      </Section>

      {/* 11. FAQ */}
      <Section title="Questions coaches ask" eyebrow="FAQ" isMobile={isMobile}>
        <div style={{ border: `1px solid ${LINE}`, borderRadius: 12, overflow: "hidden" }}>
          {CERT_FAQ.map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={f.q} style={{ borderTop: i ? `1px solid ${LINE}` : "none", background: CARD }}>
                <button onClick={() => { setOpenFaq(open ? null : i); if (!open) logEvent("cert_sales_faq_open", { q: i }); }} style={rowBtn}>
                  <span style={{ flex: 1, textAlign: "left", fontWeight: 600 }}>{f.q}</span>
                  <ChevronDown size={18} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", color: DIM }} />
                </button>
                {open && <div style={{ padding: "0 20px 18px", color: MID, fontSize: 14, lineHeight: 1.65 }}>{f.a}</div>}
              </div>
            );
          })}
        </div>
      </Section>

      {/* 12. Still deciding: call, email, or a smaller first step */}
      <Section title="Still deciding?" eyebrow="We're here" isMobile={isMobile}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <div style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 12, padding: 22 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Talk to our team</div>
            <p style={{ ...para, marginTop: 0 }}>A 20-minute call to ask anything. No pressure, no script.</p>
            <button onClick={() => openCall("still_deciding")} style={{ ...ghostBtn, display: "inline-flex", gap: 8, alignItems: "center" }}><Phone size={15} /> Book a call</button>
            <div style={{ marginTop: 14, fontSize: 13, color: DIM, display: "flex", gap: 8, alignItems: "center" }}>
              <Mail size={14} /> Or email <a href={`mailto:${CONTACT_EMAIL}?subject=BAM%20Certification%20question`} style={{ color: GOLD }}>{CONTACT_EMAIL}</a>
            </div>
          </div>
          <div style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 12, padding: 22 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Not ready for the full cert?</div>
            <p style={{ ...para, marginTop: 0 }}>
              Start with the BAM Coaches Platform for $34.99 a month: drills, practice plans, masterclasses and the coach
              community. If you buy the cert later, that month is credited back.
            </p>
            <button onClick={() => {
              logEvent("cert_sales_downsell_click", {});
              metaTrack("Lead", { content_name: "Platform from cert page" });
              try { sessionStorage.setItem("bam_post_signup_target", "#/platform"); } catch {}
              window.location.hash = "#/landing";
            }} style={ghostBtn}>See the platform</button>
          </div>
        </div>
      </Section>

      {/* Final call to action */}
      <div style={{ ...section, padding: isMobile ? "56px 18px 64px" : "80px 32px 96px", textAlign: "center" }}>
        <h2 style={{ fontFamily: HEAD, fontSize: isMobile ? 38 : 56, letterSpacing: 1, margin: "0 0 18px" }}>Ready to transform your coaching?</h2>
        <button onClick={() => scrollToPrice("final")} style={goldBtn}>Get BAM Certified <ArrowRight size={16} strokeWidth={2.5} /></button>
        <div style={{ marginTop: 26, fontSize: 12, color: DIM }}>
          Questions: <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: MID }}>{CONTACT_EMAIL}</a> · {CONTACT_PHONE} · Secure checkout by Stripe
        </div>
      </div>

      {/* Mobile: the button is always one thumb away */}
      {isMobile && (
        <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 30, background: "rgba(10,10,10,0.96)", borderTop: `1px solid ${LINE}`, padding: "12px 16px calc(12px + env(safe-area-inset-bottom))", display: "flex", gap: 10 }}>
          <button onClick={() => openCall("sticky")} style={{ ...ghostBtn, flex: "0 0 auto", padding: "12px 14px" }} aria-label="Book a call"><Phone size={16} /></button>
          <button onClick={() => scrollToPrice("sticky")} style={{ ...goldBtn, flex: 1, justifyContent: "center" }}>{price.intl ? `Enroll, ${money(price.full)} (save ${money(price.saving)})` : `Enroll, ${money(price.full)} or 3 × ${money(price.pay)}`}</button>
        </div>
      )}

      {callOpen && <CallModal onClose={() => setCallOpen(false)} />}
      <style>{`.spin{animation:certspin 1s linear infinite}@keyframes certspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function ThankYou({ guest, plan }) {
  return (
    <div style={{ background: BG, color: TEXT, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Inter, system-ui, sans-serif" }}>
      <div style={{ maxWidth: 520, textAlign: "center", background: CARD, border: `1px solid ${LINE}`, borderRadius: 16, padding: 36 }}>
        <div style={{ width: 54, height: 54, borderRadius: 27, background: `${GOLD}22`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><Check size={26} color={GOLD} /></div>
        <h1 style={{ fontFamily: HEAD, fontSize: 40, letterSpacing: 1, margin: "0 0 10px" }}>You're in</h1>
        <p style={{ ...para, marginTop: 0 }}>
          {guest
            ? "We made your BAM account with the email you paid with. Check your inbox for a link to set your password, then you're straight into the cert. It can take a couple of minutes."
            : "The BAM Certification is unlocked on your account."}
        </p>
        {plan === "3pay" && <p style={para}>You're on the 3-payment plan. It stops by itself after the third payment.</p>}
        {guest
          ? <p style={{ ...para, color: DIM, fontSize: 13 }}>No email after 10 minutes? Check spam, or write to {CONTACT_EMAIL}.</p>
          : <button onClick={() => { window.location.href = "/#/certified"; }} style={{ ...goldBtn, marginTop: 8 }}>Open my cert <ArrowRight size={16} /></button>}
      </div>
    </div>
  );
}

function PlanOption({ active, onClick, title, sub, was }) {
  return (
    <button role="radio" aria-checked={active} onClick={onClick} style={{
      padding: "14px 10px", borderRadius: 10, cursor: "pointer", background: active ? `${GOLD}18` : "transparent",
      border: `1.5px solid ${active ? GOLD : LINE}`, color: TEXT,
    }}>
      {was && <div style={{ fontSize: 13, color: DIM, textDecoration: "line-through", marginBottom: 4 }}>{was}</div>}
      <div style={{ fontFamily: HEAD, fontSize: 30, lineHeight: 1, color: active ? GOLD : TEXT }}>{title}</div>
      <div style={{ fontSize: 12, color: MID, marginTop: 6 }}>{sub}</div>
    </button>
  );
}

function Section({ title, eyebrow, children, isMobile }) {
  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: isMobile ? "44px 18px 0" : "64px 32px 0" }}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 style={{ fontFamily: HEAD, fontSize: isMobile ? 34 : 46, letterSpacing: 1, margin: "8px 0 20px" }}>{title}</h2>
      {children}
    </div>
  );
}

function Eyebrow({ children }) {
  return <div style={{ fontSize: 12, letterSpacing: 2.5, textTransform: "uppercase", color: GOLD, fontWeight: 700 }}>{children}</div>;
}

const lead = { fontSize: 17, lineHeight: 1.65, color: MID };
const para = { fontSize: 15, lineHeight: 1.7, color: MID, marginTop: 12 };
const goldBtn = {
  display: "inline-flex", alignItems: "center", gap: 8, background: GOLD, color: "#111", border: "none",
  borderRadius: 10, padding: "14px 22px", fontWeight: 800, fontSize: 15, cursor: "pointer",
};
const ghostBtn = {
  background: "transparent", color: TEXT, border: `1px solid ${LINE}`, borderRadius: 10,
  padding: "12px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer",
};
const rowBtn = {
  width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", background: "transparent",
  border: "none", color: TEXT, cursor: "pointer", fontSize: 15,
};

export default CertSalesPage;
